'use client';

import * as DialogPrimitive from '@radix-ui/react-dialog';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';

interface FilterDialogShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  clearAllLabel: ReactNode;
  confirmLabel: ReactNode;
  /** Hides the Clear All button (keeps header layout) when no filters are selected */
  showClearAll?: boolean;
  onClearAll: () => void;
  onConfirm: () => void;
  selectedChips?: ReactNode;
  children: ReactNode;
}

function FilterDialogShell({
  open,
  onOpenChange,
  title,
  clearAllLabel,
  confirmLabel,
  showClearAll = true,
  onClearAll,
  onConfirm,
  selectedChips,
  children,
}: FilterDialogShellProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/50 duration-100" />
        <DialogPrimitive.Content
          className={cn(
            'fixed left-1/2 top-1/2 z-50 w-[400px] max-w-[calc(100%-2rem)] max-h-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2',
            'flex flex-col gap-6 rounded-[16px] border border-solid border-v2-surface-tertiary bg-v2-surface-primary p-6',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 duration-100',
          )}
        >
          <div className="flex shrink-0 items-center justify-between">
            <button
              type="button"
              onClick={onClearAll}
              aria-hidden={!showClearAll}
              tabIndex={showClearAll ? 0 : -1}
              className={cn(
                'flex h-8 cursor-pointer items-center py-2 text-[12px] font-normal leading-4 tracking-[-0.12px] text-v2-btn-secondary-text outline-none transition-opacity hover:opacity-80',
                !showClearAll && 'pointer-events-none invisible',
              )}
            >
              {clearAllLabel}
            </button>
            <DialogPrimitive.Title className="text-[24px] font-semibold leading-8 text-v2-text-primary">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Close
              aria-label="Close"
              className="flex size-10 cursor-pointer items-center justify-center rounded-[10px] text-v2-icon-primary outline-none transition-colors hover:bg-v2-btn-secondary"
            >
              <X size={24} strokeWidth={1.75} />
            </DialogPrimitive.Close>
          </div>

          {selectedChips}

          <motion.div
            layout
            className="scrollbar-hide flex min-h-0 max-h-[500px] w-full flex-col gap-3 overflow-y-auto pr-1"
          >
            {children}
          </motion.div>

          <Button appearance="accent" size="M" className="w-full shrink-0" onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

const filterSectionClass =
  'flex flex-col gap-3 pb-4 border-b border-solid border-v2-border last:border-0 last:pb-0';
const filterSectionHeadingClass = 'text-[15px] font-semibold leading-5 text-v2-text-primary';

export type { FilterDialogShellProps };
export { FilterDialogShell, filterSectionClass, filterSectionHeadingClass };
